import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, RefreshCw } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import CancelOrderModal from './CancelOrderModal';
import { useToast } from './shared/Toast';
import { API_BASE_URL } from './config/api';
import { formatMoney } from '../utils/formatMoney';
import { cancellationPrintWarning } from '../utils/cancellationPrint';
import { LoadFailed } from './settings/instantSettings';
import { fmtDM, fmtDMY, fmtDayMonth, fmtLongDate, fmtNum, fmtTime, toAthensYmd, ymdToDate } from './home/format';
import {
  ChipTone, Column, DANGER_GHOST_BTN, DataTable, DateRange, DateRangeValue, DetailPanel, EmptyState, FilterSelect,
  GHOST_BTN, KeyValue, KeyValueItem, ListPage, ListToolbar, Stat, Stats, StatusChip, presetRange, todayYmd,
} from './shared/listPage';

/**
 * «Παραγγελίες» του διαχειριστή καταστήματος — διάταξη «Πίνακας» (14/10/2026). Η περίοδος στην κεφαλίδα,
 * όπως στην Αρχική (ίδιο χειριστήριο, ημέρες Ελλάδας, η μέρα γραμμένη στον υπότιτλο)· από κάτω αναζήτηση
 * και φίλτρα, τα σύνολα σε μία γραμμή, ο πίνακας και ένα συρτάρι με την παραγγελία: κατάσταση, πληρωμή,
 * παραστατικό, είδη και «Ακύρωση».
 *
 * Ίδια λογική με πριν (και με το πάνελ του org_admin): GET /api/orders/:storeId/orders-list?from&to
 * [&include_failed=1][&platform][&status][&order_type][&q] — τα φίλτρα στον server, ώστε το όριο των 500
 * να «κόβει» ΜΕΤΑ το φιλτράρισμα· η αναζήτηση φεύγει 350ms αφού σταματήσεις να γράφεις. Προεπιλογή: μόνο
 * οι σημερινές. Ακύρωση με το κοινό CancelOrderModal (ίδιο με τις Παραγγελίες του ταμείου): POST
 * …/orders/:id/cancel { reason, force } — στο 409 requires_force το παράθυρο δείχνει την προειδοποίηση του
 * παραστατικού και ξαναστέλνει με force=true (ακυρώνεται και η απόδειξη, ακυρωτικό ΑΑΔΕ). Οι αποτυχημένες
 * προσπάθειες κάρτας (phantom) κρύβονται, με «Εμφάνιση».
 *
 * Τα είδη του συρταριού έρχονται από το υπάρχον GET /api/orders/:storeId/order/:orderId (μόνο ανάγνωση,
 * ίδια εξουσιοδότηση με τη λίστα — το ίδιο που διαβάζουν το Delivery και το παράθυρο κλήσης), σε κάθε
 * άνοιγμα του συρταριού.
 */

export interface StoreOrderRow {
  id: number;
  short_code: string | null;
  order_type: string | null;
  status: string;
  total_amount: number | string | null;
  payment_type: string | null;
  platform_name: string | null;
  delivery_method?: string | null;
  order_timestamp: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  customer_name: string | null;
  phantom?: boolean;
  has_fiscal?: boolean;
}

interface OrderItem {
  product_name: string;
  quantity: number | string | null;
  unit_price?: number | string | null;
  total_price?: number | string | null;
  notes?: string | null;
}

interface OrderDetail {
  customer_phone?: string | null;
  delivery_address?: string | null;
  street_address?: string | null;
  area?: string | null;
  floor?: string | null;
  doorbell?: string | null;
  special_instructions?: string | null;
  items?: OrderItem[];
}

type DetailState = { state: 'loading' } | { state: 'ready'; data: OrderDetail } | { state: 'error' };
type LoadState = 'ready' | 'error' | 'forbidden';

// Οι τιμές των φίλτρων είναι αυτές που καταλαβαίνει ο server (ίδιες με πριν).
const PLATFORMS = ['internal', 'efood', 'wolt', 'box'] as const;
const STATUSES = ['completed', 'cancelled', 'pending', 'accepted', 'ready', 'dispatched', 'delivered', 'rejected'] as const;
const TYPES = ['takeaway', 'delivery'] as const;

// Πράσινο μόνο για ό,τι τελείωσε, πορτοκαλί για ό,τι περιμένει, κόκκινο για ακύρωση· τα ενδιάμεσα στο
// χρώμα της εφαρμογής.
const STATUS_TONE: Record<string, ChipTone> = {
  completed: 'ok',
  delivered: 'ok',
  accepted: 'brand',
  ready: 'brand',
  dispatched: 'brand',
  pending: 'warn',
  pending_accept: 'warn',
  cancelled: 'danger',
  rejected: 'danger',
};
const CANCELLED = ['cancelled', 'rejected'];

const TYPE_KEY: Record<string, string> = {
  takeaway: 'takeaway', take_away: 'takeaway', delivery: 'delivery', dine_in: 'dineIn', dinein: 'dineIn', preorder: 'preorder',
};

/** Ίδια αντιστοίχιση με τις Παραγγελίες του ταμείου (OrdersPage) + IRIS· άγνωστη τιμή = null (γράφεται αυτούσια). */
const paymentKey = (payment: string | null | undefined): string | null => {
  const v = String(payment || '').toLowerCase();
  if (v === 'cash') return 'cash';
  if (v === 'credit') return 'credit';
  if (v === 'card' || v === 'pos') return 'card';
  if (v === 'iris') return 'iris';
  if (['online', 'prepaid', 'card_online', 'credit_card'].includes(v)) return 'online';
  return null;
};

const QTY = new Intl.NumberFormat('el-GR', { maximumFractionDigits: 3 });
const fmtQty = (n: number | string | null | undefined): string => QTY.format(Number(n) || 0);

const codeOf = (o: StoreOrderRow): string => o.short_code || String(o.id);
const amountOf = (o: StoreOrderRow): number => Number(o.total_amount) || 0;
const cancellable = (o: StoreOrderRow): boolean => !o.phantom && !CANCELLED.includes(o.status);

/** «14/10 · 12:34» σε ώρα Ελλάδας, 24ωρη (ως 14/10 ο browser έγραφε «12:34 μ.μ.»). */
const whenOf = (iso: string | null | undefined): string => {
  const ymd = toAthensYmd(iso);
  const time = fmtTime(iso);
  return ymd && time ? `${fmtDM(ymd)} · ${time}` : '—';
};

/**
 * Ο υπότιτλος: «Τετάρτη 14 Οκτωβρίου», «8–14 Οκτωβρίου», «28 Σεπτεμβρίου – 4 Οκτωβρίου» — ίδια γραφή με
 * τον υπότιτλο της Αρχικής (home/HomePage rangeText).
 */
export function periodText(from: string, to: string, lang?: string): string {
  if (!from || !to) return '';
  if (from === to) return fmtLongDate(from, lang);
  const a = ymdToDate(from);
  const b = ymdToDate(to);
  if (a.getUTCFullYear() !== b.getUTCFullYear()) return `${fmtDMY(from)} – ${fmtDMY(to)}`;
  if (a.getUTCMonth() === b.getUTCMonth()) return `${a.getUTCDate()}–${fmtDayMonth(to, lang)}`;
  return `${fmtDayMonth(from, lang)} – ${fmtDayMonth(to, lang)}`;
}

/**
 * Διάστημα με ένα μόνο όριο (από την «Προσαρμογή…»): ο server θέλει και τα δύο — χωρίς «Έως» βάζει το
 * σήμερα, χωρίς «Από» επίσης το σήμερα (και τότε δεν έβγαινε τίποτα). Συμπληρώνεται εδώ, ώστε αυτό που
 * γράφει το χειριστήριο να είναι αυτό που ζητήθηκε: «Από 1/10» = 1/10 ως σήμερα, «Έως 5/10» = η 5/10.
 */
export function completeRange(range: DateRangeValue, today: string = todayYmd()): DateRangeValue {
  const from = range.from || range.to || today;
  const to = range.to || (range.from ? today : from);
  return { from, to: to < from ? from : to };
}

const StoreOrdersPanel: React.FC = () => {
  const { t, i18n } = useTranslation(['storeOrders', 'orders', 'settings', 'common', 'listPage']);
  const lang = i18n?.language;
  const { store } = useAuth();
  const storeId = store?.id;
  const { showToast } = useToast();
  const [range, setRange] = useState<DateRangeValue>(() => presetRange('today'));
  const [platform, setPlatform] = useState('');
  const [status, setStatus] = useState('');
  const [orderType, setOrderType] = useState('');
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState(''); // η τιμή που στέλνεται (μετά την καθυστέρηση)
  const [rows, setRows] = useState<StoreOrderRow[]>([]);
  const [count, setCount] = useState(0);
  const [revenue, setRevenue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>('ready');
  const [busy, setBusy] = useState<number | null>(null);
  const [showFailed, setShowFailed] = useState(false);
  const [phantomCount, setPhantomCount] = useState(0);
  const [selected, setSelected] = useState<StoreOrderRow | null>(null);
  const [details, setDetails] = useState<Record<number, DetailState>>({});
  const [cancelTarget, setCancelTarget] = useState<StoreOrderRow | null>(null);
  // Γρήγορες αλλαγές φίλτρων: μετρά μόνο η απάντηση του τελευταίου αιτήματος.
  const seq = useRef(0);
  const itemsHeadingId = useId();

  const authHeaders = { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` };

  // Η αναζήτηση δεν στέλνει αίτημα σε κάθε πλήκτρο.
  useEffect(() => {
    const timer = setTimeout(() => setQLive(q), 350);
    return () => clearTimeout(timer);
  }, [q]);

  const { from, to } = range;
  const load = useCallback(async () => {
    if (!storeId) return;
    const id = ++seq.current;
    setLoading(true);
    const clear = () => {
      setRows([]);
      setPhantomCount(0);
      setCount(0);
      setRevenue(0);
    };
    try {
      const params = new URLSearchParams({ from, to });
      if (showFailed) params.set('include_failed', '1');
      if (platform) params.set('platform', platform);
      if (status) params.set('status', status);
      if (orderType) params.set('order_type', orderType);
      if (qLive.trim()) params.set('q', qLive.trim());
      const res = await fetch(`${API_BASE_URL}/api/orders/${storeId}/orders-list?${params}`, { headers: authHeaders });
      if (id !== seq.current) return;
      if (!res.ok) {
        clear();
        setLoadState(res.status === 403 ? 'forbidden' : 'error');
        return;
      }
      const data = await res.json();
      if (id !== seq.current) return;
      setRows(data.orders || []);
      setPhantomCount(data.phantom_count || 0);
      setCount(data.count || 0);
      setRevenue(data.revenue || 0);
      setLoadState('ready');
    } catch {
      if (id !== seq.current) return;
      clear();
      setLoadState('error');
    } finally {
      if (id === seq.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, from, to, showFailed, platform, status, orderType, qLive]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setPlatform(''); setStatus(''); setOrderType(''); setQ(''); };
  const hasFilters = !!(platform || status || orderType || q);

  // Τα είδη της παραγγελίας του συρταριού, σε κάθε άνοιγμα (μια παραγγελία μπορεί να διορθωθεί στο μεταξύ).
  // Ό,τι φορτώθηκε ήδη φαίνεται αμέσως και απλώς ανανεώνεται· σκελετός/σφάλμα μόνο όταν δεν υπάρχει τίποτα.
  const fetchDetail = useCallback(async (orderId: number) => {
    if (!storeId) return;
    const keep = (prev: Record<number, DetailState>) => prev[orderId]?.state === 'ready';
    setDetails(prev => (keep(prev) ? prev : { ...prev, [orderId]: { state: 'loading' } }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${storeId}/order/${orderId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` },
      });
      if (!res.ok) throw new Error(String(res.status));
      const data: OrderDetail = await res.json();
      setDetails(prev => ({ ...prev, [orderId]: { state: 'ready', data } }));
    } catch {
      setDetails(prev => (keep(prev) ? prev : { ...prev, [orderId]: { state: 'error' } }));
    }
  }, [storeId]);

  const selectedId = selected?.id ?? null;
  useEffect(() => {
    if (selectedId !== null) fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  // Μετά από ανανέωση το συρτάρι δείχνει τη νέα εικόνα της ίδιας παραγγελίας.
  const current = selected ? (rows.find(r => r.id === selected.id) ?? selected) : null;

  // Ακύρωση με το κοινό CancelOrderModal (ίδιο UX με τις Παραγγελίες του ταμείου): προκαθορισμένες αιτίες,
  // και στο 409 requires_force το παράθυρο δείχνει την προειδοποίηση του παραστατικού και ξαναστέλνει με
  // force=true (ακυρώνεται η παραγγελία ΚΑΙ η απόδειξη, ακυρωτικό ΑΑΔΕ). Από το συρτάρι: το συρτάρι κλείνει
  // πρώτα — το παράθυρο ακύρωσης είναι από κάτω του (z-50 < 70) και θα έμενε κρυμμένο.
  const openCancel = (o: StoreOrderRow) => {
    setSelected(null);
    setCancelTarget(o);
  };
  const confirmCancel = async (reason: string, force?: boolean) => {
    if (!storeId || !cancelTarget) return;
    setBusy(cancelTarget.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${storeId}/orders/${cancelTarget.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ reason, force: force === true }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body?.requires_force && !force) {
          const err: any = new Error(body.message || '');
          err.requiresForce = true;
          err.fiscalCanCancel = body.fiscal_can_cancel === true;
          throw err;
        }
        throw new Error(body?.message || t('storeOrders:cancel.failed'));
      }
      const fc = body?.fiscal_cancel;
      if (fc && fc.done !== true && fc.error !== 'no_mark') {
        showToast('error', t('storeOrders:cancel.fiscalNotIssued', { error: fc.error || t('storeOrders:cancel.fiscalErrorFallback') }));
      }
      const printWarning = fc?.done === true ? cancellationPrintWarning(fc, t) : null;
      if (printWarning) showToast('warning', printWarning);
      await load();
    } finally {
      setBusy(null);
    }
  };

  const typeText = useCallback((o: StoreOrderRow): string | null => {
    const key = o.order_type ? TYPE_KEY[o.order_type] : undefined;
    return key ? t(`storeOrders:types.${key}`) : (o.order_type || null);
  }, [t]);

  const platformText = useCallback((o: StoreOrderRow): string | null => {
    const p = o.platform_name;
    if (!p || p === 'internal') return null;
    return (PLATFORMS as readonly string[]).includes(p) ? t(`storeOrders:platforms.${p}`) : p;
  }, [t]);

  const channelText = useCallback((o: StoreOrderRow): string => (
    [typeText(o), platformText(o)].filter(Boolean).join(' · ') || '—'
  ), [typeText, platformText]);

  const paymentText = (o: StoreOrderRow): string | null => {
    const key = paymentKey(o.payment_type);
    return key ? t(`storeOrders:payment.${key}`) : (o.payment_type || null);
  };

  const statusChip = useCallback((o: StoreOrderRow) => {
    if (o.phantom) {
      return <StatusChip tone="muted" title={t('storeOrders:phantom.chipTitle')}>{t('storeOrders:phantom.chip')}</StatusChip>;
    }
    const tone = STATUS_TONE[o.status] ?? 'muted';
    const label = STATUS_TONE[o.status] ? t(`storeOrders:status.${o.status}`) : (o.status || '—');
    return <StatusChip tone={tone}>{label}</StatusChip>;
  }, [t]);

  const columns = useMemo<Column<StoreOrderRow>[]>(() => [
    {
      key: 'code',
      header: t('storeOrders:columns.code'),
      width: '96px',
      stack: 'title',
      render: o => <span className="font-medium tabular-nums">{codeOf(o)}</span>,
      stackRender: o => codeOf(o),
    },
    {
      key: 'time',
      header: t('storeOrders:columns.time'),
      width: '116px',
      render: o => <span className="tabular-nums text-t2 whitespace-nowrap">{whenOf(o.order_timestamp)}</span>,
      stackRender: o => whenOf(o.order_timestamp),
    },
    {
      key: 'channel',
      header: t('storeOrders:columns.channel'),
      width: 'minmax(112px, 1fr)',
      render: o => {
        const type = typeText(o);
        const plat = platformText(o);
        if (!type && !plat) return <span className="text-t4">—</span>;
        return (
          <>
            {type && <span>{type}</span>}
            {plat && <span className="text-t3">{type ? ' · ' : ''}{plat}</span>}
          </>
        );
      },
      stackRender: o => channelText(o),
    },
    {
      key: 'customer',
      header: t('storeOrders:columns.customer'),
      width: 'minmax(0, 1.3fr)',
      render: o => (o.customer_name ? <span>{o.customer_name}</span> : <span className="text-t4">—</span>),
      stackRender: o => o.customer_name || '',
    },
    {
      key: 'amount',
      header: t('storeOrders:columns.amount'),
      width: '104px',
      align: 'right',
      stack: 'end',
      render: o => <span className="font-medium">{formatMoney(amountOf(o))}</span>,
      stackRender: o => formatMoney(amountOf(o)),
    },
    {
      key: 'fiscal',
      header: t('storeOrders:columns.fiscal'),
      width: '116px',
      hideBelow: 'medium',
      render: o => (o.has_fiscal
        ? <span className="text-t2" title={t('storeOrders:fiscal.issuedTitle')}>{t('storeOrders:fiscal.issued')}</span>
        : <span className="text-t4" title={t('storeOrders:fiscal.noneTitle')}>—</span>),
    },
    {
      key: 'status',
      header: t('storeOrders:columns.status'),
      width: '148px',
      render: statusChip,
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('storeOrders:columns.actions')}</span>,
      width: '104px',
      align: 'right',
      hideBelow: 'narrow',
      // Η ακύρωση μένει ένα πάτημα από τη γραμμή, όπως πριν· χωρίς κόκκινο κουτί σε κάθε γραμμή — κόκκινο
      // μόνο στο hover. Το κλικ στη γραμμή ανοίγει τα στοιχεία.
      render: o => (cancellable(o) ? (
        <button
          type="button"
          disabled={busy === o.id}
          onClick={() => openCancel(o)}
          aria-label={t('storeOrders:cancel.buttonLabel', { code: codeOf(o) })}
          className="inline-flex items-center min-h-[36px] px-2.5 rounded-lg text-sm font-medium text-t2 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-500/10 whitespace-nowrap transition-colors disabled:opacity-50"
        >
          {busy === o.id ? t('storeOrders:cancel.busy') : t('storeOrders:cancel.button')}
        </button>
      ) : null),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, typeText, platformText, channelText, statusChip, busy]);

  const statusHint = (o: StoreOrderRow): React.ReactNode => {
    if (o.phantom) return t('storeOrders:phantom.hint');
    if (!CANCELLED.includes(o.status)) return undefined;
    const parts = [
      o.cancelled_at ? t('storeOrders:detail.cancelledAt', { when: whenOf(o.cancelled_at) }) : null,
      o.cancel_reason ? t('storeOrders:detail.reason', { reason: o.cancel_reason }) : null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : undefined;
  };

  const detailItems = (o: StoreOrderRow, detail: OrderDetail | null): KeyValueItem[] => {
    const phone = detail?.customer_phone || null;
    const address = detail && TYPE_KEY[o.order_type || ''] === 'delivery'
      ? ([detail.street_address, detail.area].filter(Boolean).join(', ') || detail.delivery_address || null)
      : null;
    const addressHint = detail && address
      ? [
        detail.floor ? t('storeOrders:detail.floor', { floor: detail.floor }) : null,
        detail.doorbell ? t('storeOrders:detail.doorbell', { doorbell: detail.doorbell }) : null,
      ].filter(Boolean).join(' · ')
      : '';
    return [
      { id: 'status', label: t('storeOrders:detail.status'), value: statusChip(o), hint: statusHint(o) },
      {
        id: 'customer',
        label: t('storeOrders:detail.customer'),
        value: o.customer_name || phone,
        hint: o.customer_name && phone ? <span className="tabular-nums">{phone}</span> : undefined,
      },
      ...(address ? [{ id: 'address', label: t('storeOrders:detail.address'), value: address, hint: addressHint || undefined }] : []),
      { id: 'payment', label: t('storeOrders:detail.payment'), value: paymentText(o) },
      { id: 'amount', label: t('storeOrders:detail.amount'), value: formatMoney(amountOf(o)) },
      {
        id: 'fiscal',
        label: t('storeOrders:detail.fiscal'),
        value: o.has_fiscal ? t('storeOrders:fiscal.issued') : t('storeOrders:fiscal.none'),
      },
    ];
  };

  const detailSubtitle = (o: StoreOrderRow): string => {
    const ymd = toAthensYmd(o.order_timestamp);
    const time = fmtTime(o.order_timestamp);
    const when = ymd && time ? `${fmtLongDate(ymd, lang)} · ${time}` : null;
    return [when, channelText(o)].filter(x => x && x !== '—').join(' · ');
  };

  const itemsBlock = (o: StoreOrderRow) => {
    const d = details[o.id];
    let body: React.ReactNode;
    if (!d || d.state === 'loading') {
      body = (
        <div className="space-y-3 pt-2" aria-busy="true">
          <span className="sr-only" role="status">{t('storeOrders:detail.itemsLoading')}</span>
          {['72%', '54%', '64%'].map(w => <span key={w} className="lp-skel" style={{ width: w }} aria-hidden="true" />)}
        </div>
      );
    } else if (d.state === 'error') {
      body = (
        <p role="alert" className="pt-1 text-sm text-t2">
          {t('storeOrders:detail.itemsFailed')}{' '}
          <button type="button" className="xs-link" onClick={() => fetchDetail(o.id)}>{t('common:tryAgain')}</button>
        </p>
      );
    } else {
      const items = Array.isArray(d.data.items) ? d.data.items : [];
      body = (
        <>
          {items.length === 0 ? (
            <p className="pt-1 text-sm text-t3">{t('storeOrders:detail.itemsEmpty')}</p>
          ) : (
            <ul className="divide-y divide-line" data-testid="order-detail-items">
              {items.map((item, i) => (
                <li key={`${i}-${item.product_name}`} className="flex items-start justify-between gap-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm text-t1">
                      <span className="tabular-nums text-t3">{fmtQty(item.quantity)} ×</span> {item.product_name}
                    </p>
                    {item.notes && <p className="mt-0.5 text-xs text-t3 whitespace-pre-line">{item.notes}</p>}
                  </div>
                  <span className="shrink-0 text-sm font-medium text-t1 tabular-nums">
                    {item.total_price !== null && item.total_price !== undefined && item.total_price !== ''
                      ? formatMoney(Number(item.total_price) || 0)
                      : '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {d.data.special_instructions && (
            <p className="mt-3 text-[13px] leading-[19px] text-t3">
              <span className="font-medium text-t2">{t('storeOrders:detail.comment')}:</span>{' '}
              <span className="whitespace-pre-line">{d.data.special_instructions}</span>
            </p>
          )}
        </>
      );
    }
    return (
      <section className="mt-7" aria-labelledby={itemsHeadingId}>
        <h3 id={itemsHeadingId} className="pb-2 text-sm font-semibold text-t1 border-b border-line-strong">{t('storeOrders:detail.items')}</h3>
        {body}
      </section>
    );
  };

  // Μόνο η πρώτη φόρτωση κρύβει τα σύνολα (σκελετός)· μετά μένουν όσο ξαναφορτώνει, όπως οι γραμμές.
  const firstLoad = !loaded && loading;
  const detail = current ? details[current.id] : undefined;

  return (
    <ListPage
      eyebrow={t('settings:nav.groups.overview')}
      title={t('orders:title')}
      subtitle={periodText(from, to, lang)}
      actions={<DateRange from={from} to={to} allowAll={false} onChange={r => setRange(completeRange(r))} />}
      testId="store-orders"
    >
      <ListToolbar
        search={{
          value: q,
          onChange: setQ,
          placeholder: t('storeOrders:search.placeholder'),
          ariaLabel: t('storeOrders:search.label'),
        }}
        filters={(
          <>
            <FilterSelect
              label={t('storeOrders:filters.platform')}
              value={platform}
              onChange={setPlatform}
              allLabel={t('storeOrders:filters.allPlatforms')}
              options={PLATFORMS.map(v => ({ value: v, label: t(`storeOrders:platforms.${v}`) }))}
              testId="orders-filter-platform"
            />
            <FilterSelect
              label={t('storeOrders:filters.status')}
              value={status}
              onChange={setStatus}
              allLabel={t('storeOrders:filters.allStatuses')}
              options={STATUSES.map(v => ({ value: v, label: t(`storeOrders:statusFilter.${v}`) }))}
              testId="orders-filter-status"
            />
            <FilterSelect
              label={t('storeOrders:filters.type')}
              value={orderType}
              onChange={setOrderType}
              allLabel={t('storeOrders:filters.allTypes')}
              options={TYPES.map(v => ({ value: v, label: t(`storeOrders:types.${v}`) }))}
              testId="orders-filter-type"
            />
          </>
        )}
        actions={(
          <button type="button" className={GHOST_BTN} onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('common:refresh')}
          </button>
        )}
      />

      {/* Τα σύνολα του φιλτραρισμένου συνόλου (όπως τα δίνει ο server) και, όταν φιλτράρεις, ο «Καθαρισμός» —
          εδώ και όχι δίπλα στα φίλτρα: εκεί η γραμμή εργαλείων θα άλλαζε θέση τη στιγμή που διαλέγεις φίλτρο.
          Το μέγεθος γραμμάτων μπαίνει στο κουτί: το .xs-link παίρνει «font: inherit» (sheet.css, μετά το Tailwind). */}
      {loadState === 'ready' && !firstLoad && (
        <div className="mb-3 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-[13.5px] leading-5">
          <div role="status" data-testid="orders-stats">
            <Stats>
              <Stat label={t('storeOrders:stats.orders')} value={fmtNum(count)} />
              <Stat label={t('storeOrders:stats.revenue')} value={formatMoney(revenue)} hint={t('storeOrders:stats.revenueHint')} />
              {count >= 500 && <span className="lp-stat-hint">{t('storeOrders:stats.capped')}</span>}
            </Stats>
          </div>
          {hasFilters && (
            <button type="button" className="xs-link" onClick={clearFilters}>{t('storeOrders:filters.clear')}</button>
          )}
        </div>
      )}

      {/* Οι αποτυχημένες προσπάθειες κάρτας δεν είναι πωλήσεις: κρυμμένες, με «Εμφάνιση». */}
      {loadState === 'ready' && phantomCount > 0 && (
        <p className="mb-3 text-[13px] leading-[19px] text-t3" data-testid="orders-phantom">
          {showFailed
            ? t('storeOrders:phantom.shown', { count: phantomCount })
            : t('storeOrders:phantom.hidden', { count: phantomCount })}{' '}
          <button type="button" className="xs-link" onClick={() => setShowFailed(v => !v)}>
            {showFailed ? t('storeOrders:phantom.hide') : t('storeOrders:phantom.show')}
          </button>
        </p>
      )}

      {loadState !== 'ready' ? (
        <LoadFailed
          message={loadState === 'forbidden' ? t('storeOrders:forbidden') : t('storeOrders:loadFailed')}
          onRetry={() => load()}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={o => o.id}
          onRowClick={setSelected}
          selectedKey={current?.id ?? null}
          loading={loading}
          stickyHeader
          rowLabel={o => t('storeOrders:rowLabel', { code: codeOf(o), when: whenOf(o.order_timestamp) })}
          rowClassName={o => (o.phantom ? 'opacity-60' : undefined)}
          empty={(
            <EmptyState
              icon={ClipboardList}
              title={t('storeOrders:empty.title')}
              hint={hasFilters ? t('storeOrders:empty.hintFilters') : t('storeOrders:empty.hintPeriod')}
              action={hasFilters
                ? <button type="button" className={GHOST_BTN} onClick={clearFilters}>{t('storeOrders:filters.clear')}</button>
                : undefined}
            />
          )}
          testId="orders-table"
        />
      )}

      <DetailPanel
        open={current !== null}
        onClose={() => setSelected(null)}
        title={current ? t('storeOrders:detail.title', { code: codeOf(current) }) : ''}
        subtitle={current ? detailSubtitle(current) : undefined}
        footerStart={current && cancellable(current) ? (
          <button type="button" className={DANGER_GHOST_BTN} disabled={busy === current.id} onClick={() => openCancel(current)}>
            {t('storeOrders:cancel.drawerButton')}
          </button>
        ) : undefined}
        footer={<button type="button" className={GHOST_BTN} onClick={() => setSelected(null)}>{t('listPage:close')}</button>}
        testId="order-detail"
      >
        {current && (
          <>
            <KeyValue items={detailItems(current, detail?.state === 'ready' ? detail.data : null)} testId="order-detail-values" />
            {itemsBlock(current)}
          </>
        )}
      </DetailPanel>

      <CancelOrderModal
        isOpen={!!cancelTarget}
        orderCode={cancelTarget?.short_code || String(cancelTarget?.id || '')}
        platformName={cancelTarget?.platform_name}
        onClose={() => setCancelTarget(null)}
        onConfirm={confirmCancel}
      />
    </ListPage>
  );
};

export default StoreOrdersPanel;
